import { SectionCard } from "../../../../Component/AgentComponents/SectionCard";
import BlueButton from "../../../../Component/Common/BlueButton";
import RefreshButton from "../../../../Component/Common/RefreshButton";
import SimpleTable from "../../../../Component/Common/SimpleTable";
import RegularLoader from "../../../../Component/Loader/RegularLoader";
import { formatDateTime } from "../../../../Component/Common/formatDateTime";
import { formatCurrency } from "../../../../Component/AdminComponents/formatCurrency";
import { CheckCircle2, Clock, Layers, XCircle } from "lucide-react";

export default function WarrantyTable({
  showOnlyOpen,
  loadingDevices,
  deviceRows,
  currentPage,
  totalPages,
  setCurrentPage,
  fetchDevices,
  Navigate,
  approvalFilter,
  setApprovalFilter,
  search,
  setSearch,
}) {

  const approvalMap = {
  0: { label: "Provisioning", variant: "warning" },
  1: { label: "Active", variant: "success" },
  2: { label: "Rejected", variant: "danger" },
};
  if (showOnlyOpen) return null;

  return (
    <SectionCard title="Warranty Registry (ARP + Customer)">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end gap-3 mb-3">
        {/* Search */}
        <div>
          <input
            type="text"
            placeholder="Search by IMEI, warranty, customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-full lg:w-64 rounded-xl border border-slate-300 px-3 text-sm"
          />

          {search.length > 0 && search.length < 4 && (
            <p className="text-xs text-slate-500 mt-1">
              Type at least 4 characters to search
            </p>
          )}
        </div>


        {/* ================= FILTERS ================= */}
        <div className="flex items-center gap-3 flex-wrap">

          {/* 🔵 MOBILE ICON FILTERS */}
          <div className="flex md:hidden items-center gap-3">
            {[
              { key: "all", icon: Layers, active: "bg-blue-600 border-blue-600", text: "text-blue-600" },
              { key: "active", icon: CheckCircle2, active: "bg-emerald-600 border-emerald-600", text: "text-emerald-600" },
              { key: "provisioning", icon: Clock, active: "bg-amber-500 border-amber-500", text: "text-amber-500" },
              { key: "rejected", icon: XCircle, active: "bg-rose-600 border-rose-600", text: "text-rose-600" },
            ].map(({ key, icon: Icon, active, text }) => {
              const isActive = approvalFilter === key;

              return (
                <button
                  key={key}
                  onClick={() => setApprovalFilter(key)}
                  className={`h-10 w-10 flex items-center justify-center rounded-full border transition-all duration-200
                    ${
                      isActive
                        ? `${active} text-white shadow-lg scale-105`
                        : `bg-white border-slate-300 ${text} hover:bg-slate-50`
                    }
                  `}
                >
                  <Icon size={18} />
                </button>
              );
            })}
          </div>


          {/* 🖥 DESKTOP TEXT FILTERS */}
          <div className="hidden md:flex flex-wrap gap-2">
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "provisioning", label: "Provisioning" },
              { key: "rejected", label: "Rejected" },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => setApprovalFilter(item.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition
                  ${
                    approvalFilter === item.key
                      ? item.key === "active"
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : item.key === "provisioning"
                        ? "bg-amber-500 text-white border-amber-500"
                        : item.key === "rejected"
                        ? "bg-rose-600 text-white border-rose-600"
                        : "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }
                `}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* 🔄 Refresh Button */}
          <RefreshButton onClick={fetchDevices} />
        </div>

      </div>


      {/* LOADING */}
      {loadingDevices && <RegularLoader />}

      {/* NO DATA */}
      {!loadingDevices && (!deviceRows || deviceRows.length === 0) && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <p className="text-sm font-semibold text-slate-600">
            No warranty records found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Try refreshing or adjusting your filters.
          </p>
        </div>
      )}

        {/* ================= MOBILE CARD VIEW ================= */}
        {!loadingDevices && deviceRows?.length > 0 && (
          <div className="md:hidden space-y-4">
            {deviceRows.map((row) => {
              const d = row.raw;

              const approval = approvalMap[d.is_approved] || {
                label: "Unknown",
                variant: "gray",
              };

              const statusColor =
                d.is_approved === 1
                  ? "border-l-emerald-500"
                  : d.is_approved === 0
                  ? "border-l-amber-500"
                  : d.is_approved === 2
                  ? "border-l-rose-500"
                  : "border-l-blue-500";

              const badgeColor =
                d.is_approved === 1
                  ? "bg-emerald-100 text-emerald-700"
                  : d.is_approved === 0
                  ? "bg-amber-100 text-amber-700"
                  : d.is_approved === 2
                  ? "bg-rose-100 text-rose-700"
                  : "bg-blue-100 text-blue-700";

              return (
                <div
                  key={d.id}
                  className={`bg-white rounded-2xl border border-slate-200 border-l-4 ${statusColor} p-4 shadow-sm`}
                >
                  {/* HEADER */}
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 leading-snug">
                        {d.product_name || "Warranty Plan"}
                      </h3>

                      <p className="text-xs text-slate-500 mt-1">
                        Warranty ID: {d.w_code}
                      </p>
                    </div>

                    <span
                      className={`px-3 py-1 text-xs rounded-full font-medium ${badgeColor}`}
                    >
                      {approval.label}
                    </span>
                  </div>

                  {/* INVOICE + SOLD ON */}
                  <div className="grid grid-cols-2 gap-4 mt-4 text-xs">
                    <div>
                      <p className="text-slate-500">IMEI</p>
                      <p className="font-semibold text-slate-900">
                        {d.imei1 || d.serial || "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-slate-500">Sold On</p>
                      <p className="font-semibold text-slate-900">
                        {formatDateTime(d.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* AMOUNT BOX */}
                  <div className="mt-4 bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Amount</p>
                    <p className="text-xl font-semibold text-slate-900">
                      ₹ {formatCurrency(d.product_mrp || 0)}
                    </p>
                  </div>

                  {/* CUSTOMER INFO */}
                  <div className="grid grid-cols-2 gap-4 mt-4 text-xs">
                    <div>
                      <p className="text-slate-500">Customer</p>
                      <p className="font-semibold text-slate-900">
                        {d.customer?.name || "—"}
                      </p>
                    </div>

                    <div>
                      <p className="text-slate-500">Retailer</p>
                      <p className="font-semibold text-slate-900">
                        {d.customer?.retailer?.business_name || "—"}
                      </p>
                    </div>
                  </div>

                  {/* REJECT REASON */}
                  {d.is_approved === 2 && (
                    <div className="mt-4 bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs">
                      <p className="font-medium text-rose-700">Reject Reason</p>
                      <p className="text-rose-600 mt-1">
                        {d.reject_reason || "-"}
                      </p>
                    </div>
                  )}

                  {/* VIEW BUTTON */}
                  <div className="mt-5">
                    <BlueButton
                      onClick={() =>
                        Navigate(`/warranty-details/${d.id}`)
                      }
                      className="w-full py-2 rounded-xl"
                    >
                      View Details
                    </BlueButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}


      {/* TABLE */}
      {!loadingDevices && deviceRows?.length > 0 && (
        <div className="hidden md:block">
        <SimpleTable
          headers={[
            "Warranty",
            "ARP / Customer",
            "Device",
            "Plan",
            "Status",
          ]}
          rows={deviceRows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          showSrNo
          renderActions={(row) => (
            <BlueButton
              onClick={() =>
                Navigate(`/warranty-details/${row.id}`)
              }
              // className="px-3 py-1 bg-blue-600 text-white rounded-md"
            >
              View
            </BlueButton>
          )}
        />
        </div>
      )}
    </SectionCard>
  );
}



// import { SectionCard } from "../../../../Component/AgentComponents/SectionCard";
// import RefreshButton from "../../../../Component/Common/RefreshButton";
// import SimpleTable from "../../../../Component/Common/SimpleTable";
// import RegularLoader from "../../../../Component/Loader/RegularLoader";

// export default function WarrantyTable({
//   showOnlyOpen,
//   loadingDevices,
//   deviceRows,
//   currentPage,
//   totalPages,
//   setCurrentPage,
//   fetchDevices,
//   Navigate,
// }) {
//   if (showOnlyOpen) return null;

//   return (
//     <SectionCard title="Warranty Registry (Retailer + Customer)">
//       <div className="flex justify-end">
//         <RefreshButton onClick={fetchDevices} />
//       </div>

//       {loadingDevices ? (
//         <RegularLoader />
//       ) : (
//         <SimpleTable
//           headers={["Warranty", "Retailer / Customer", "Device", "Plan", "Status"]}
//           rows={deviceRows}
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={setCurrentPage}
//           showSrNo
//           renderActions={(row) => (
//             <button
//               onClick={() => Navigate(`/warranty-details/${row.w_customer_id}`)}
//               className="px-3 py-1 bg-blue-600 text-white rounded-md"
//             >
//               View
//             </button>
//           )}
//         />
//       )}
//     </SectionCard>
//   );
// }
